import { createContext, useContext, ReactNode } from 'react';

import { ActionHandlers } from '../components/DeployedPipelinesTable/cellRenderers';
import { useDeploymentMenuActionHandlers } from '../hooks/useDeploymentMenuActionHandlers';
import {
  DeploymentDataItem,
  useDeploymentsData
} from '../hooks/useDeploymentsData';
import type { DeploymentSelect, DeploymentsPage } from '../Types';

import { EnvironmentType } from '~/components/EnvironmentIndicator';
import { Pipeline } from '~/entities/Pipeline';

export type DeploymentsContextValue = {
  loading: boolean;
  deploymentsList?: DeploymentsPage;
  current: string | null;
  environment: string;

  deploymentsData: DeploymentDataItem[];
  menuActionHandlers: ActionHandlers;

  onShowModalDeployment: (deployment: DeploymentSelect) => void;
  goToPipeline: (pipelineId: string) => void;
  remove: ({
    environment,
    realm,
    deployment
  }: {
    environment: string;
    realm: string;
    deployment: string;
  }) => Promise<void>;
  redeploy: (pipeline: Pipeline) => void;
  openDetailPromote: (deployment: DeploymentSelect) => void;
  openModalRollbackDeploy: (deployment: DeploymentSelect) => void;
  onViewExecutions: (pipelineName: string) => void;

  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onInfinityScroll: () => void;
};

const DeploymentsContext = createContext<DeploymentsContextValue | null>(null);

type DeploymentsProviderProps = {
  children: ReactNode;
  loading: boolean;
  deploymentsList?: DeploymentsPage;
  current: string | null;
  environment: string;
  activeRealm: string;
  onShowModalDeployment: (deployment: DeploymentSelect) => void;
  goToPipeline: (pipelineId: string) => void;
  remove: ({
    environment,
    realm,
    deployment
  }: {
    environment: string;
    realm: string;
    deployment: string;
  }) => Promise<void>;
  redeploy: (pipeline: Pipeline) => void;
  openDetailPromote: (deployment: DeploymentSelect) => void;
  openModalRollbackDeploy: (deployment: DeploymentSelect) => void;
  onViewExecutions: (pipelineName: string) => void;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onInfinityScroll: () => void;
};

export function DeploymentsProvider({
  children,
  loading,
  deploymentsList,
  current,
  environment,
  activeRealm,
  onShowModalDeployment,
  goToPipeline,
  remove,
  redeploy,
  openDetailPromote,
  openModalRollbackDeploy,
  onViewExecutions,
  onPageChange,
  rowsPerPage,
  onInfinityScroll
}: DeploymentsProviderProps) {
  const deploymentsData = useDeploymentsData(
    deploymentsList?.content || [],
    environment as EnvironmentType,
    activeRealm
  );

  const menuActionHandlers = useDeploymentMenuActionHandlers({
    onShowModalDeployment,
    goToPipeline,
    remove,
    redeploy,
    openDetailPromote,
    openModalRollbackDeploy,
    onViewExecutions
  });

  const value: DeploymentsContextValue = {
    loading,
    deploymentsList,
    current,
    environment,
    deploymentsData,
    menuActionHandlers,
    onShowModalDeployment,
    goToPipeline,
    remove,
    redeploy,
    openDetailPromote,
    openModalRollbackDeploy,
    onViewExecutions,
    onPageChange,
    rowsPerPage,
    onInfinityScroll
  };

  return (
    <DeploymentsContext.Provider value={value}>
      {children}
    </DeploymentsContext.Provider>
  );
}

export function useDeploymentsContext() {
  const context = useContext(DeploymentsContext);
  if (!context) {
    throw new Error(
      'useDeploymentsContext must be used within a DeploymentsProvider'
    );
  }
  return context;
}
